import { yupResolver } from "@hookform/resolvers/yup"
import { faBoxOpen } from "@jewlr/pro-light-svg-icons/faBoxOpen"
import {
  Box,
  Button,
  Flex,
  FontAwesomeIcon,
  Typography,
} from "@jewlr/storybook/core"
import { FloatInput } from "@jewlr/storybook/form"
import PropTypes from "prop-types"
import { useState } from "react"
import { useForm, Controller } from "react-hook-form"
import { connect } from "react-redux"
import { Link } from "react-router-dom"
import styled from "styled-components"
import isEmail from "validator/lib/isEmail"
import * as Yup from "yup"

import { subscribeNotification } from "areas/product/store/actions"

const Container = styled(Box)`
  background-color: ${(props) => props.theme.colors.white};
  border-radius: 12px;
  margin-bottom: 10px;
  padding: 30px 24px;
  text-align: center;
  ${(props) => props.theme.mediaQueries.tablet`
    background-color: ${props.theme.colors.grey[4]};
    border-radius: 0;
    padding: 24px;
    margin-bottom: 20px;
  `}
`

const CTA = styled(Button)`
  background: ${(props) => props.theme.colors.black};
  border-radius: 5px;
  color: ${(props) => props.theme.colors.white};
  font-size: 18px;
  font-weight: 700;
  height: 54px;
  margin-top: 16px;
  width: 100%;
  &:focus,
  &:hover {
    color: ${(props) => props.theme.colors.white};
  }
`

const Msg = styled(Typography).attrs((props) => ({
  color: props.success ? "greenDarker" : "error",
  fontSize: "14px",
  letterSpacing: "-0.03em",
}))``

const schema = Yup.object().shape({
  email: Yup.string()
    .required("Please enter your email.")
    .test("test-email", "Please enter your email.", (value) =>
      isEmail(value || "")
    ),
})

const OutOfStockForm = ({ outOfStockRef, subscribeNotification }) => {
  const [success, setSuccess] = useState(false)

  const {
    control,
    formState: { errors, isSubmitting },
    handleSubmit,
    setError,
  } = useForm({
    defaultValues: {
      email: "",
    },
    mode: "onBlur",
    resolver: yupResolver(schema),
  })

  const onSubmit = async ({ email }) => {
    try {
      setSuccess(false)

      await new Promise((res, rej) => {
        subscribeNotification(email, {
          onError: (err) => rej(err),
          onSuccess: () => {
            setSuccess(true)
            res()
          },
        })
      })
    } catch (err) {
      setError("root.error", {
        message: err?.errors?.[0] || err?.message || err,
      })
    }
  }

  return (
    <Container ref={outOfStockRef}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Flex alignItems="center" justifyContent="center" mb="10px">
          <FontAwesomeIcon
            color="black"
            fontSize="24px"
            icon={faBoxOpen}
            mr="6px"
          />
          <Typography
            bold
            color="black"
            fontSize="22px"
            letterSpacing="-0.03em"
          >
            OUT OF STOCK
          </Typography>
        </Flex>
        <Typography
          color="grey.65"
          fontSize="14px"
          letterSpacing="-0.03em"
          lineHeight="16px"
          pb="12px"
        >
          Sign up! We&apos;ll notify you when this item is back.
        </Typography>
        {success ? (
          <>
            <Msg success>Success! We&apos;ll be in touch soon.</Msg>
            <CTA forwardedAs={Link} mt={2} to="/new-items">
              Shop New Items
            </CTA>
          </>
        ) : (
          <>
            {errors?.root?.error && <Msg>{errors.root.error.message}</Msg>}
            <Box pt={1} textAlign="left">
              <Controller
                control={control}
                name="email"
                render={({ field, fieldState: { error } }) => (
                  <FloatInput
                    autoComplete="email"
                    data-cy="out-of-stock-email"
                    error={error}
                    label="Enter Your Email"
                    placeholder="Enter Your Email"
                    type="email"
                    variant="checkout"
                    {...field}
                  />
                )}
              />
            </Box>
            <CTA disabled={isSubmitting} loading={isSubmitting} type="submit">
              {!isSubmitting && "Notify Me When Available"}
            </CTA>
          </>
        )}
      </form>
    </Container>
  )
}

OutOfStockForm.propTypes = {
  outOfStockRef: PropTypes.object,
  subscribeNotification: PropTypes.func,
}

const mapStateToProps = () => ({})

const mapDispatchToProps = (dispatch) => {
  return {
    subscribeNotification: (email, actions) =>
      dispatch(subscribeNotification(email, actions)),
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(OutOfStockForm)
